<script setup>
// NOTICE: KAPTION-INTEGRATION
// rumo-actions.PROTOCOL.1 — agents see the buttons the assistant offered, read-only,
// with the customer's pick marked. Agents can't send buttons from here.
// rumo-navigation.MESSAGE.1 — navigate/link buttons show the destination id they
// open (they never send anything to the chat, so they are never "chosen").
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  items: { type: Array, default: () => [] },
  suggestions: { type: Array, default: () => [] },
  chosenValue: { type: [String, Number], default: null },
});

const { t } = useI18n();

const options = computed(() =>
  props.items
    .filter(item => item && item.title)
    .map((item, index) => ({
      key: `${index}-${item.value ?? item.action?.destination ?? ''}`,
      title: item.title,
      value: String(item.value ?? ''),
      destination: ['navigate', 'link'].includes(item.action?.type)
        ? item.action.destination
        : null,
      chosen:
        props.chosenValue !== null &&
        String(item.value) === String(props.chosenValue),
    }))
);

const chips = computed(() =>
  props.suggestions.filter(suggestion => suggestion && suggestion.title)
);
</script>

<template>
  <div class="mt-3 flex flex-col gap-2" data-kaption="offered-options">
    <span class="text-xs text-n-slate-11">
      {{ t('CONVERSATION.KAPTION_OFFERED_OPTIONS.OFFERED') }}
    </span>
    <ul class="flex flex-col gap-1.5">
      <li
        v-for="option in options"
        :key="option.key"
        class="flex items-center justify-between gap-2 rounded-lg border px-3 py-1.5 text-sm"
        :class="
          option.chosen
            ? 'border-n-brand bg-n-brand/10 text-n-slate-12 font-medium'
            : 'border-n-weak text-n-slate-11'
        "
        :aria-current="option.chosen ? 'true' : undefined"
      >
        <span>{{ option.title }}</span>
        <span
          v-if="option.destination"
          class="font-mono text-xs text-n-slate-10"
          :title="t('CONVERSATION.KAPTION_OFFERED_OPTIONS.OPENS')"
        >
          {{ `→ ${option.destination}` }}
        </span>
        <span v-if="option.chosen" class="text-xs text-n-brand">
          {{ t('CONVERSATION.KAPTION_OFFERED_OPTIONS.CHOSEN') }}
        </span>
      </li>
    </ul>
    <template v-if="chips.length">
      <span class="text-xs text-n-slate-11">
        {{ t('CONVERSATION.KAPTION_OFFERED_OPTIONS.SUGGESTIONS') }}
      </span>
      <div class="flex flex-wrap gap-1.5">
        <span
          v-for="chip in chips"
          :key="chip.title"
          class="rounded-full border border-n-weak px-2.5 py-0.5 text-xs text-n-slate-11"
        >
          {{ chip.title }}
        </span>
      </div>
    </template>
  </div>
</template>
